import { CheckSquare } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger, TooltipProvider } from '@/components/ui/tooltip'

interface Props {
  fvsAprovadas: number
  fvsReprovadas: number
  fvsPendentes: number
  fvsPorObra: Record<string, { aprovadas: number; reprovadas: number; pendentes: number }>
  obraNames: Record<string, string>
}

export default function DashboardFVSConformidade({ fvsAprovadas, fvsReprovadas, fvsPendentes, fvsPorObra, obraNames }: Props) {
  const total = fvsAprovadas + fvsReprovadas + fvsPendentes
  const indice = total > 0 ? Math.round((fvsAprovadas / total) * 100) : 0
  const corIndice = indice >= 80 ? 'var(--emerald)' : indice >= 50 ? 'var(--amber)' : 'var(--rose)'

  return (
    <TooltipProvider>
      <div className="rounded-xl p-4 border" style={{ background: 'hsl(var(--bg2))', borderColor: 'hsl(var(--line))' }}>
        <div className="flex items-center gap-2 mb-4">
          <CheckSquare size={14} className="text-primary" />
          <span className="text-sm font-bold text-foreground">Conformidade FVS</span>
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="text-[10px] text-muted-foreground cursor-help ml-1">ⓘ</span>
            </TooltipTrigger>
            <TooltipContent><p className="text-xs max-w-[200px]">Índice = FVS aprovadas / total de FVS. Quanto maior, melhor a conformidade da qualidade.</p></TooltipContent>
          </Tooltip>
        </div>

        {/* Consolidado */}
        <div className="flex items-center gap-4 mb-4">
          <div className="text-center">
            <div className="text-3xl font-extrabold" style={{ color: `hsl(${corIndice})` }}>{indice}%</div>
            <div className="text-[10px] text-muted-foreground">Conformidade</div>
          </div>
          <div className="flex-1 space-y-1.5">
            {[
              { lbl: 'Aprovadas', val: fvsAprovadas, cor: 'var(--emerald)' },
              { lbl: 'Reprovadas', val: fvsReprovadas, cor: 'var(--rose)' },
              { lbl: 'Pendentes', val: fvsPendentes, cor: 'var(--amber)' },
            ].map(item => (
              <div key={item.lbl} className="flex items-center gap-2">
                <div className="w-2 h-2 rounded-full" style={{ background: `hsl(${item.cor})` }} />
                <span className="text-[10px] text-muted-foreground flex-1">{item.lbl}</span>
                <span className="text-xs font-bold" style={{ color: `hsl(${item.cor})` }}>{item.val}</span>
                {total > 0 && (
                  <div className="w-16 h-1 rounded-full overflow-hidden" style={{ background: 'hsl(var(--bg5))' }}>
                    <div className="h-full rounded-full" style={{ width: `${(item.val / total) * 100}%`, background: `hsl(${item.cor})` }} />
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Por obra */}
        {Object.keys(fvsPorObra).length > 0 && (
          <div className="border-t pt-3 space-y-1.5" style={{ borderColor: 'hsl(var(--line))' }}>
            <div className="text-[10px] font-bold text-muted-foreground uppercase tracking-wider mb-2">Por obra</div>
            {Object.entries(fvsPorObra).map(([obraId, d]) => {
              const t = d.aprovadas + d.reprovadas + d.pendentes
              const idx = t > 0 ? Math.round((d.aprovadas / t) * 100) : 0
              const c = idx >= 80 ? 'var(--emerald)' : idx >= 50 ? 'var(--amber)' : 'var(--rose)'
              return (
                <div key={obraId} className="flex items-center gap-2">
                  <span className="text-xs text-foreground truncate flex-1 max-w-[140px]">{obraNames[obraId] || '—'}</span>
                  <div className="w-20 h-1 rounded-full overflow-hidden" style={{ background: 'hsl(var(--bg5))' }}>
                    <div className="h-full rounded-full" style={{ width: `${idx}%`, background: `hsl(${c})` }} />
                  </div>
                  <span className="text-[10px] font-bold min-w-[32px] text-right" style={{ color: `hsl(${c})` }}>{idx}%</span>
                </div>
              )
            })}
          </div>
        )}
      </div>
    </TooltipProvider>
  )
}
